import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { overrides } from './theme';

/**
 * Redux Store: Web-App-Header
 **/

const initialState = {
  id: 'web-app-header',
  c1Accounts: {
    requestAccounts: false,
    c1AccountID: '',
    c1AccountsList: [],
    lastAccessedAccountID: '',
  },
  c1Services: { fetchServices: false, hasError: false, c1ServicesList: [] },
  c1Token: { token: '', expires: '', hasError: false, fetchC1Token: false },
  c1UserDetails: { name: '', email: '' },
  c1Common: {
    isLoading: false,
    wsLoggedIn: false, // WS session state during dual sign-in phase
    wsNavigationRestricted: false, // WS session state during rescue login sign-in phase
    loggedIn: false,
    loggingOut: false,
    authDetails: { authType: '', expires: '' },
    hasError: false,
    appSelection: '',
    locale: 'en',
    errorMsg: '',
    theme: { colorMode: 'light', overrides: overrides },
    showSupportModal: false,
  },
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState?.c1Common,
          theme: {
            ...state.c1Common.theme,
            ...action.shellInitState?.c1Common?.theme,
            overrides: {
              ...state.c1Common.theme.overrides,
              ...action.shellInitState?.c1Common?.theme?.overrides,
            },
          },
          storeId: 'web-app-header',
        },
      };
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'NAVIGATE_APP':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          appSelection: action.appName,
        },
      };
    case 'TOGGLE_GLOBAL_LOADING_INDICATOR':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          toggleGlobalLoader: action.loading,
        },
      };
    case 'WS_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
        },
      };
    case 'TOGGLE_DISPLAY_SUPPORT_MODAL':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          showSupportModal: !state.c1Common.showSupportModal,
        },
      };
    case 'WS_RESCUE_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
          wsNavigationRestricted: true,
        },
      };
    case 'WS_LOGGED_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: false,
          wsNavigationRestricted: false,
        },
      };
    case 'LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: true,
          loggingOut: false,
        },
      };
    case 'LOGGING_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: false,
          loggingOut: true,
        },
      };
    case 'ADD_AUTH_DETAILS':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          authDetails: {
            ...state.authDetails,
            ...action.authDetails,
          },
        },
      };
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...state.c1Token,
          ...action.c1Token,
        },
      };
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
    case 'ADD_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          lastAccessedAccountID: state.c1Accounts.c1AccountID,
          c1AccountID: action.c1AccountID,
        },
      };
    case 'REMOVE_ACCOUNT':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: state.c1Accounts.c1AccountsList.filter(
            (account) => account.id !== action.c1AccountID
          ),
        },
      };
    case 'UPDATE_ACCOUNT':
      const { accountId, accountAlias, mfaRequired, locale, timezone } = action.accountDetails;
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: state.c1Accounts.c1AccountsList.map((account) => {
            if (account.id === accountId) {
              account.alias = accountAlias;
              account.mfaRequired = mfaRequired;
              account.locale = locale;
              account.timezone = timezone;
            }
            return account;
          }),
        },
      };
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'ADD_USER_INFO': {
      const { name = '', email = '', timezone = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          email,
          name,
          timezone,
        },
      };
    }
    case 'STORE_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          c1ServicesList: action.c1ServicesList,
          hasError: false,
          errorMsg: '',
          fetchServices: false,
        },
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
        c1Common: {
          ...initialState.c1Common,
          locale: state.c1Common.locale,
        },
      };
    default:
      return state;
  }
}

export const getCommonType = (state = {}, key = '') => {
  const c1CommonState = state?.c1Common;
  if (c1CommonState[key] !== undefined) {
    return c1CommonState[key];
  } else {
    for (const objProp in c1CommonState) {
      if (c1CommonState[objProp] && c1CommonState[objProp][key]) {
        return c1CommonState[objProp][key];
      }
    }
  }
};

export const getAccountsList = (state) => state.c1Accounts.c1AccountsList;
export const getUserDetails = (state, type) =>
  type ? state.c1UserDetails[type] : state.c1UserDetails;
export const getServices = (state, type) => state?.c1Services[type];
/**
 * Get role
 * Camel Case used for display role specific strings
 * */
export const getRole = (state, isCamelCase) => {
  const role = state?.c1Token['role']?.split('/')[1];
  return role ? (isCamelCase ? role.replace(/-([a-z])/g, (g) => g[1].toUpperCase()) : role) : '';
};

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  console.group(action.type, 'web-app-header');
  console.debug('web-app-header dispatching', action);

  // Call the next dispatch method in the middleware chain
  const result = next(action);

  console.debug('web-app-header next state after dispatch', store.getState());
  console.groupEnd();
  return result;
};

const composeEnhancers = composeWithDevTools({ name: 'web-app-header' });
export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));
